import { useEffect, useState } from "react";
import { MessageSquare, CheckCircle2, Clock, Ban } from "lucide-react";
import { smsApi, pct, fone } from "./api";
import { ACENTO } from "./cores";
import KpisSMS from "./KpisSMS";
import RecargaPopup from "./RecargaPopup";
import GraficoRosca from "./GraficoRosca";
import GraficoLinha from "./GraficoLinha";
import RankingCampanhas from "./RankingCampanhas";
import { PainelCarregando } from "../Carregando";

/**
 * Painel do SMS, sem filtro de periodo (decisao do dono): mostra o todo.
 *
 * O protagonista e' a tabela de campanhas — cada linha e' o resumo completo
 * (enviadas, gasto, faturamento, ROI) e clica pro relatorio. Os graficos
 * apoiam; o que repetia numero dos cards de cima saiu.
 */
// `versao` muda a cada disparo concluído/cancelado: recarrega os números sem
// voltar ao carregamento com cards (que é só da primeira abertura de cada canal).
export default function DashboardSMS({ onAbrirCampanha, canal = "sms", versao = 0 }) {
  const ehVoz = canal === "voz";
  const [d, setD] = useState(null);
  const [erro, setErro] = useState(null);
  const [recargaAberta, setRecargaAberta] = useState(false);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    // Sucesso limpa o erro de uma falha anterior (o painel não remonta mais a cada envio)
    smsApi.dashboard(3650, canal).then((r) => { setD(r); setErro(null); })
      .catch(() => setErro("Não foi possível carregar o painel."));
  }, [refresh, canal, versao]);

  if (erro) return <div className="p-6 text-destructive">{erro}</div>;
  if (!d) return <PainelCarregando />;

  const { faturamento: fat, envio, carteira } = d;

  return (
    <div className="space-y-5">
      <KpisSMS canal={canal} carteira={carteira} envio={envio} faturamento={fat}
        onAdicionarSaldo={() => setRecargaAberta(true)} />

      {recargaAberta && (
        <RecargaPopup
          onFechar={() => setRecargaAberta(false)}
          onCreditado={() => setRefresh((v) => v + 1)}
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* ------------------------------------------------ taxa de entrega */}
        <div className="rounded-xl border bg-card p-5">
          <h3 className="font-semibold mb-4">{ehVoz ? "Taxa de atendimento" : "Taxa de entrega"}</h3>
          {/* Rosca aqui (pedido do dono): proporcao entregue x nao entregue e'
              o que a rosca conta bem; o miolo carrega a taxa em destaque. */}
          <GraficoRosca
            rotuloTotal={ehVoz ? "ligações" : "mensagens"} rotuloResto="Outros status"
            centroValor={pct(ehVoz ? envio.taxa_atendimento_decimos : envio.taxa_entrega_decimos)}
            centroRotulo={ehVoz ? "atendidas" : "de entrega"}
            itens={ehVoz ? [
              { id: "atendidas", nome: "Atendidas", valor: envio.atendidas },
              { id: "nao-atendidas", nome: "Não atendidas", valor: envio.nao_atendidas || 0 },
              // A SMSMais congela quase tudo em "Called" — mostrar como "não
              // atendida" seria mentira; discada sem retorno e' o fato.
              { id: "sem-resposta", nome: "Discadas (sem retorno)", valor: envio.sem_resposta || 0 },
            ] : [
              { id: "entregue", nome: "Total entregue", valor: envio.entregues },
              { id: "nao-entregue", nome: "Total não entregue", valor: envio.falhas },
            ]} />
        </div>

        <div className="rounded-xl border bg-card p-5">
          <h3 className="font-semibold mb-4">{ehVoz ? "Andamento das ligações" : "Andamento dos envios"}</h3>
          <p className="text-4xl font-bold tabular-nums">
            {(envio.entregues + envio.falhas + envio.na_fila).toLocaleString("pt-BR")}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {ehVoz ? "ligações no total" : "mensagens no total"}
          </p>
          <div className="mt-4 space-y-2.5 text-sm">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 shrink-0" style={{ color: ACENTO }} />
              <span className="text-muted-foreground flex-1">{ehVoz ? "Realizadas" : "Enviadas"}</span>
              <span className="tabular-nums font-semibold">
                {(envio.entregues + envio.falhas).toLocaleString("pt-BR")}
                <span className="text-xs text-muted-foreground ml-1.5">
                  ({Math.round(((envio.entregues + envio.falhas) /
                     Math.max(envio.entregues + envio.falhas + envio.na_fila, 1)) * 100)}%)
                </span>
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 shrink-0 text-amber-500" />
              <span className="text-muted-foreground flex-1">{ehVoz ? "Pendentes" : "Pendentes de envio"}</span>
              <span className="tabular-nums font-semibold">
                {envio.na_fila.toLocaleString("pt-BR")}
                <span className="text-xs text-muted-foreground ml-1.5">
                  ({Math.round((envio.na_fila /
                     Math.max(envio.entregues + envio.falhas + envio.na_fila, 1)) * 100)}%)
                </span>
              </span>
            </div>
            {!ehVoz && <div className="flex items-center gap-2">
              <Ban className="w-4 h-4 shrink-0 text-amber-500" />
              <span className="text-muted-foreground flex-1">Pediram para sair</span>
              <span className="tabular-nums font-semibold">
                {(envio.optouts || 0).toLocaleString("pt-BR")}
                <span className="text-xs text-muted-foreground ml-1.5">
                  {envio.enviados ? `(${pct(envio.taxa_optout_decimos)})` : ""}
                </span>
              </span>
            </div>}
          </div>
        </div>

        {/* ---------------------------------------------- ultimas mensagens */}
        <div className="rounded-xl border bg-card p-5">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-muted-foreground" /> {ehVoz ? "Últimas ligações" : "Últimas mensagens"}
          </h3>
          {!d.ultimas_mensagens.length ? (
            <p className="text-sm text-muted-foreground py-6 text-center">Nada enviado ainda.</p>
          ) : (
            <ul className="divide-y -my-2">
              {d.ultimas_mensagens.slice(0, 6).map((m, i) => (
                <li key={i} className="py-2 flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm truncate">
                      {m.cliente_nome || fone(m.telefone)}
                    </p>
                    <p className="text-xs text-muted-foreground truncate tabular-nums">
                      {m.cliente_nome ? `${fone(m.telefone)} · ` : ""}{m.campanha}
                    </p>
                  </div>
                  <span className={`text-[11px] rounded-full px-2 py-0.5 shrink-0 ${
                    ehVoz
                      ? (m.entrega_voz === "Answered" ? "bg-emerald-500/10 text-emerald-500"
                        : m.entrega_voz === "Called" ? "bg-sky-500/10 text-sky-500"
                        : m.entrega_voz ? "bg-red-500/10 text-red-500"
                        : "bg-muted text-muted-foreground")
                      : (m.delivery === "Delivered" ? "bg-emerald-500/10 text-emerald-500"
                        : m.delivery ? "bg-red-500/10 text-red-500"
                        : "bg-muted text-muted-foreground")}`}>
                    {ehVoz
                      ? (m.entrega_voz === "Answered" ? "Atendida"
                        : m.entrega_voz === "Called" ? "Discada"
                        : m.entrega_voz === "Not Answered" ? "Não atendida"
                        : m.entrega_voz ? "Falhou" : "Na fila")
                      : (m.delivery === "Delivered" ? "Entregue" : m.delivery ? "Não entregue" : "Na fila")}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* ------------------------------------------- campanhas (protagonista) */}
      <RankingCampanhas ranking={d.ranking} ehVoz={ehVoz} onAbrirCampanha={onAbrirCampanha} />

      <GraficoLinha dias={d.volume_por_dia} titulo={ehVoz ? "Volume de ligações" : "Volume de envios"} />
    </div>
  );
}
